import { useMemo } from "react";
import { BootSplash } from "../components/BootSplash";
import { ComingSoonDirections } from "../components/coming-soon/ComingSoonDirections";
import { ComingSoonEventGrid } from "../components/coming-soon/ComingSoonEventGrid";
import { ComingSoonEyebrow } from "../components/coming-soon/ComingSoonEyebrow";
import { EventCancellationBanner } from "../components/EventCancellationBanner";
import { ComingSoonReplay } from "../components/coming-soon/ComingSoonReplay";
import { useEventContentOptional } from "../context/EventContentContext";
import { useSiteCopy } from "../context/SiteCopyContext";
import { useCountdown } from "../hooks/useCountdown";
import { useLiveInfo } from "../hooks/useLiveInfo";
import {
  hasFeaturedSchedule,
  NEXT_GATHERING_LABEL,
  NEXT_GATHERING_SUB,
} from "../lib/eventDisplay";
import {
  applyHomepageVideoToLiveInfo,
  homepageVideoFromCopy,
  shouldShowHomepageVideo,
} from "../lib/homepage-video";
import { isBroadcastLive } from "../lib/live-display";

/**
 * Permanent public home (`/`). Always the event landing stack (poster / RSVP /
 * directions / replay). Marketing HomePage lives at `/home` only — never mount
 * it here (avoids flash).
 */
export function RootLandingPage() {
  const content = useEventContentOptional();
  const liveInfo = useLiveInfo();
  const { copy } = useSiteCopy();
  const homepageVideo = homepageVideoFromCopy(copy);
  const displayLiveInfo = applyHomepageVideoToLiveInfo(liveInfo, homepageVideo);
  const live = isBroadcastLive(liveInfo);
  const showStream = shouldShowHomepageVideo(liveInfo, homepageVideo);
  const dateIso = content?.date ?? "";
  const target = useMemo(
    () => (dateIso ? Date.parse(dateIso) : NaN),
    [dateIso],
  );
  const { done } = useCountdown(Number.isFinite(target) ? target : Date.now());

  // Wait for Layout's event fetch — never briefly mount HomePage.
  if (!content) {
    return <BootSplash progress={0.55} />;
  }

  const hasSchedule = hasFeaturedSchedule(content);

  return (
    <div className="root-live-landing">
      <ComingSoonEyebrow isLive={live} />
      {content.cancellation && (
        <div className="event-cancel-banner-wrap event-cancel-banner-wrap--in-landing">
          <EventCancellationBanner notice={content.cancellation} />
        </div>
      )}
      <ComingSoonEventGrid
        liveInfo={displayLiveInfo}
        eventId={content.eventId}
        eventPhase={content.eventPhase}
        dateIso={content.date}
        posterUrl={content.posterUrl}
        posterCaption={content.eventTitle}
        showLiveVideo={showStream}
        showWhen={hasSchedule}
        whenLabel={hasSchedule ? undefined : NEXT_GATHERING_LABEL}
        whenSub={hasSchedule ? undefined : NEXT_GATHERING_SUB}
        showCountdown={hasSchedule && !done}
        showRsvp={hasSchedule || !content.cancellation}
        thanksIso={hasSchedule ? content.date : undefined}
        thanksLine={
          hasSchedule ? undefined : "See you at the next worship overnight."
        }
      />
      <ComingSoonDirections venueMap={content.venueMap ?? null} />
      <ComingSoonReplay
        liveInfo={displayLiveInfo}
        showLiveVideo={showStream}
        posterUrl={content.posterUrl}
      />
    </div>
  );
}
